import {
  HHBox,
  HHButton,
  HHCircularProgress,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Add, CloudOff, Refresh, Storage } from '@mui/icons-material';
import { useProviders } from '../hooks/use-providers';
import { ProviderCard } from './provider-card';

type ProviderDashboardProps = {
  onProviderClick: (source: string) => void;
  onCreateNewProvider: () => void;
};

export const ProviderDashboard = ({
  onProviderClick,
  onCreateNewProvider,
}: ProviderDashboardProps): JSX.Element => {
  const { providers, loading, error } = useProviders();

  if (loading) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={8} height="100%">
          {/* Enhanced Header - Same as loaded state */}
          <HHBox
            sx={{
              position: 'relative',
              marginBottom: '16px',
              paddingX: '2rem',
              paddingTop: '2rem',
            }}
          >
            <HHStack direction="row" alignItems="center" spacing={3}>
              <HHBox
                sx={{
                  backgroundColor: '#1565C0',
                  background:
                    'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(21, 101, 192, 0.25)',
                }}
              >
                <Storage
                  sx={{
                    fontSize: '32px',
                    color: 'white',
                  }}
                />
              </HHBox>
              <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                <HHTypography
                  hhVariant="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '28px', sm: '32px', md: '36px' },
                    lineHeight: 1.2,
                    background:
                      'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                    letterSpacing: '-0.02em',
                  }}
                >
                  File Ingestion Providers
                </HHTypography>
                <HHTypography
                  hhVariant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '16px',
                    fontWeight: 400,
                    marginTop: '4px',
                  }}
                >
                  Manage file ingestion configs and configurations for data
                  providers
                </HHTypography>
              </HHStack>
            </HHStack>

            {/* Decorative accent line */}
            <HHBox
              sx={{
                position: 'absolute',
                bottom: '-12px',
                left: '64px',
                width: '100px',
                height: '4px',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                borderRadius: '2px',
                opacity: 0.6,
              }}
            />
          </HHBox>

          {/* Modern Loading Section */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '400px',
            }}
          >
            {/* Animated Loading Container */}
            <HHBox
              sx={{
                position: 'relative',
                marginBottom: 4,
              }}
            >
              {/* Outer Ring */}
              <HHBox
                sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '3px solid #E3F2FD',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />

              {/* Main Spinner */}
              <HHCircularProgress
                size={80}
                thickness={3}
                sx={{
                  color: '#1565C0',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />

              {/* Inner Icon */}
              <HHBox
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'translate(-50%, -50%) scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'translate(-50%, -50%) scale(1.1)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <Storage
                  sx={{
                    fontSize: '32px',
                    color: '#1565C0',
                  }}
                />
              </HHBox>
            </HHBox>

            {/* Loading Text */}
            <HHTypography
              hhVariant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                marginBottom: 1,
              }}
            >
              Loading Providers
            </HHTypography>

            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '400px',
                lineHeight: 1.6,
              }}
            >
              Fetching your data provider configurations...
            </HHTypography>

            {/* Loading Progress Dots */}
            <HHStack direction="row" spacing={1} sx={{ marginTop: 3 }}>
              {[0, 1, 2].map(index => (
                <HHBox
                  key={index}
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#1565C0',
                    animation: `dotBounce 1.5s ease-in-out ${index * 0.2}s infinite`,
                    '@keyframes dotBounce': {
                      '0%, 80%, 100%': {
                        transform: 'scale(0.8)',
                        opacity: 0.5,
                      },
                      '40%': {
                        transform: 'scale(1.2)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              ))}
            </HHStack>
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  if (error) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={6} height="100%">
          {/* Error Content */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '500px',
            }}
          >
            {/* Error Icon */}
            <HHBox
              sx={{
                backgroundColor: '#FFEBEE',
                background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                borderRadius: '50%',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 3,
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
                border: '2px solid #FFCDD2',
              }}
            >
              <CloudOff
                sx={{
                  fontSize: '64px',
                  color: '#D32F2F',
                }}
              />
            </HHBox>

            {/* Error Title */}
            <HHTypography
              hhVariant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '28px', sm: '32px', md: '36px' },
                lineHeight: 1.2,
                color: '#D32F2F',
                marginBottom: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Providers Unavailable
            </HHTypography>

            {/* Error Description */}
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '18px',
                lineHeight: 1.6,
                maxWidth: '500px',
                marginBottom: 4,
              }}
            >
              We couldn't load the data providers. This might be due to a
              network issue or the provider data may no longer be accessible.
            </HHTypography>

            {/* Error Details */}
            <HHBox
              sx={{
                backgroundColor: '#F5F5F5',
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: 4,
                maxWidth: '500px',
                width: '100%',
              }}
            >
              <HHStack direction="column" spacing={2}>
                <HHTypography
                  hhVariant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: 'text.primary',
                  }}
                >
                  What you can try:
                </HHTypography>
                <HHStack direction="column" spacing={1} alignItems="flex-start">
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Check your internet connection
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Refresh the page and try again
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Contact support if the problem persists
                    </HHTypography>
                  </HHStack>
                </HHStack>
              </HHStack>
            </HHBox>

            {/* Action Buttons */}
            <HHStack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <HHButton
                hhVariant="contained"
                startIcon={<Refresh />}
                onClick={(): void => window.location.reload()}
                sx={{
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#B71C1C',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Try Again
              </HHButton>
            </HHStack>

            {/* Technical Details (if available) */}
            {error && (
              <HHBox
                sx={{
                  marginTop: 4,
                  padding: '16px',
                  backgroundColor: '#FFF3E0',
                  border: '1px solid #FFB74D',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  width: '100%',
                }}
              >
                <HHTypography
                  hhVariant="caption"
                  sx={{
                    color: '#E65100',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                  }}
                >
                  Error: {error.message || 'Unknown error occurred'}
                </HHTypography>
              </HHBox>
            )}
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  const activeProviders = providers.filter(p => p.status === 'active');
  const inactiveProviders = providers.filter(p => p.status === 'inactive');

  return (
    <HHBox paddingX="0" bgcolor="#ffffff">
      <HHStack direction="column" spacing={8} height="100%">
        <HHBox
          sx={{
            position: 'relative',
            marginBottom: '16px',
            paddingX: '2rem',
            paddingTop: '2rem',
          }}
        >
          <HHStack direction="row" alignItems="center" spacing={3}>
            <HHBox
              sx={{
                backgroundColor: '#1565C0',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                borderRadius: '16px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(21, 101, 192, 0.25)',
              }}
            >
              <Storage
                sx={{
                  fontSize: '32px',
                  color: 'white',
                }}
              />
            </HHBox>
            <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
              <HHTypography
                hhVariant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '28px', sm: '32px', md: '36px' },
                  lineHeight: 1.2,
                  background:
                    'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                File Ingestion Providers
              </HHTypography>
              <HHTypography
                hhVariant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '16px',
                  fontWeight: 400,
                  marginTop: '4px',
                }}
              >
                Manage file ingestion configurations for data providers
              </HHTypography>
            </HHStack>
            <HHButton
              hhVariant="contained"
              startIcon={<Add />}
              onClick={onCreateNewProvider}
              sx={{
                backgroundColor: '#1565C0',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)',
                '&:hover': {
                  backgroundColor: '#0D47A1',
                  boxShadow: '0 6px 16px rgba(21, 101, 192, 0.4)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Create New File Config
            </HHButton>
          </HHStack>

          {/* Decorative accent line */}
          <HHBox
            sx={{
              position: 'absolute',
              bottom: '-12px',
              left: '34px',
              width: '100px',
              height: '4px',
              background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              borderRadius: '2px',
              opacity: 0.6,
            }}
          />
        </HHBox>

        {providers.length === 0 ? (
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingY: 8,
              textAlign: 'center',
              backgroundColor: '#FAFAFA',
              borderRadius: '16px',
              border: '2px dashed #E0E0E0',
              marginTop: 4,
            }}
          >
            <HHBox
              sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '50%',
                padding: '24px',
                marginBottom: 3,
              }}
            >
              <Storage
                sx={{
                  fontSize: '48px',
                  color: 'text.secondary',
                }}
              />
            </HHBox>
            <HHTypography
              hhVariant="h5"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                marginBottom: 1,
              }}
            >
              No file ingestion providers yet
            </HHTypography>
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                marginBottom: 4,
                maxWidth: '400px',
                lineHeight: 1.6,
              }}
            >
              File ingestion providers will be displayed here when configs with
              file ingestion resource types are configured
            </HHTypography>
          </HHBox>
        ) : (
          <HHBox sx={{ paddingX: '2rem' }}>
            <HHStack direction="column" spacing={4}>
              {activeProviders.length > 0 && (
                <HHBox>
                  <HHStack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ marginBottom: 3 }}
                  >
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '24px',
                        backgroundColor: '#2E7D32',
                        borderRadius: '4px',
                      }}
                    />
                    <HHTypography
                      hhVariant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    >
                      Active Providers ({activeProviders.length})
                    </HHTypography>
                  </HHStack>
                  <HHGrid container spacing={4}>
                    {activeProviders.map(provider => (
                      <HHGrid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={provider.source}
                      >
                        <ProviderCard
                          provider={provider}
                          onClick={onProviderClick}
                        />
                      </HHGrid>
                    ))}
                  </HHGrid>
                </HHBox>
              )}

              {inactiveProviders.length > 0 && (
                <HHBox>
                  <HHStack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ marginBottom: 3 }}
                  >
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '24px',
                        backgroundColor: '#757575',
                        borderRadius: '4px',
                      }}
                    />
                    <HHTypography
                      hhVariant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    >
                      Inactive Providers ({inactiveProviders.length})
                    </HHTypography>
                  </HHStack>
                  <HHGrid container spacing={4}>
                    {inactiveProviders.map(provider => (
                      <HHGrid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={provider.source}
                      >
                        <ProviderCard
                          provider={provider}
                          onClick={onProviderClick}
                        />
                      </HHGrid>
                    ))}
                  </HHGrid>
                </HHBox>
              )}
            </HHStack>
          </HHBox>
        )}
      </HHStack>
    </HHBox>
  );
};
